/**
 * API Client for backend communication
 * 
 * Uses VITE_API_BASE_URL for production builds
 * Falls back to empty string (same origin) for development with proxy
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

console.log('🔧 API Base URL:', API_BASE_URL || '(using proxy)');

/**
 * Make an API request with credentials and timeout
 * @param {string} endpoint - API endpoint (e.g., '/api/business-models')
 * @param {RequestInit} options - Fetch options
 * @returns {Promise<Response>}
 */
export async function apiRequest(endpoint, options = {}) {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const defaultOptions = {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };

  // 30 second timeout for cold starts (Render free tier)
  const timeout = 30000;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, { 
      ...defaultOptions, 
      ...options,
      signal: controller.signal 
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout - backend may be sleeping (cold start can take up to 20s)');
    }
    throw error;
  }
}

/**
 * GET request helper
 * @param {string} endpoint - API endpoint
 * @returns {Promise<any>}
 */
export async function apiGet(endpoint) {
  const response = await apiRequest(endpoint, { method: 'GET' });
  if (!response.ok) {
    throw new Error(`API Error: ${response.status} ${response.statusText}`);
  }
  return response.json();
}

/**
 * POST request helper
 * @param {string} endpoint - API endpoint
 * @param {any} data - Request body data
 * @returns {Promise<any>}
 */
export async function apiPost(endpoint, data) {
  const response = await apiRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response;
}

// ============================================================================
// Vocabulary API
// ============================================================================

export const vocabApi = {
  getBusinessModels: () => apiGet('/api/business-models'),
  getAssetClasses: () => apiGet('/api/asset-classes'),
  getOperatingSectors: () => apiGet('/api/operating-sectors'),
  getProviderTypes: () => apiGet('/api/provider-types'),
  getInvestmentStrategies: () => apiGet('/api/investment-strategies'),
  getCreditFocus: () => apiGet('/api/credit-focus'),
  getDealNature: () => apiGet('/api/deal-nature'),
  getComponentType: () => apiGet('/api/component-type'),
  getFundingInstruments: () => apiGet('/api/funding-instruments'),
  getInstrumentSpecificity: () => apiGet('/api/instrument-specificity'),
};

// ============================================================================
// Auth API
// ============================================================================

export const authApi = {
  /**
   * Get Auth0 configuration from backend
   */
  getConfig: async () => {
    const response = await apiRequest('/config/auth');
    if (!response.ok) {
      throw new Error('Failed to load auth configuration');
    }
    return response.json();
  },

  /**
   * Get current user info
   */
  getMe: async () => {
    const response = await apiRequest('/auth/me');
    return { response, data: response.ok ? await response.json() : null };
  },

  /**
   * Exchange authorization code for session
   */
  callback: async (code) => {
    const response = await apiPost('/auth/callback', { code });
    return { response, data: response.ok ? await response.json() : await response.json() };
  },

  /**
   * Logout current user
   */
  logout: async () => {
    const response = await apiPost('/auth/logout', {});
    return response;
  },
};
